import {IHttpService} from "angular";
import {TmcVideoModal} from "./tmc-video/TmcVideoModal";
import {TradingLevelWarningDialog} from "./trading-level-warning/TradingLevelWarningDialog";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {PortfolioService} from "../../../service/PortfolioService";
import {BaseStrategy} from "../types/WhatIf";
import {TradeService} from "./TradeService";
import {EquityTicketService} from "../equity-ticket/EquityTicketService";
import {SecurityUtil} from "../../../../js/util/SecurityUtil";
import {TmcCodeResponse, TmcLeg} from "./models/TmcRequests";
import {SimpleModalService} from "../../../../types/SimpleModalService";
import {AccountStore} from "../../../../account/store/AccountStore";
import {EquityOrder} from "../place-equity-order-wizard/models/EquityOrder";
import {OptionTicketDialogService} from "./dialog/OptionTicketDialogService";
import {TmcWarningDialog} from "./tmc-warning/TmcWarningDialog";
import {TmcWarningAction} from "./tmc-warning/models/TmcWarningAction";
import {OrderSide} from "../../../../orders/models/OrderRecord";
import {AccountId} from "../../../../account/types/AccountRecord";
import {TradingLevelRequest} from "./models/TradingLevelRequest";
import {TradingLevelService} from "./TradingLevelService";
import {Branding } from "../../../../types/Branding";
import {BackOffice} from "../../../../types/BackOffice";

export class PlaceOrderService {

    public static id = "placeOrderService";

    /** @ngInject */
    public constructor(private readonly optionTicketDialogService: OptionTicketDialogService,
                       private readonly simpleModalService: SimpleModalService,
                       private readonly $http: IHttpService,
                       private readonly tmcVideoModal: TmcVideoModal,
                       private readonly tradingLevelWarningDialog: TradingLevelWarningDialog,
                       private readonly currentUserService: CurrentUserService,
                       private readonly portfolioService: PortfolioService,
                       private readonly accountStore: AccountStore,
                       private readonly equityTicketService: EquityTicketService,
                       private readonly tradeService: TradeService,
                       private readonly tradingLevelService: TradingLevelService,
                       private readonly tmcWarningDialog: TmcWarningDialog) {
    }

    /* Because the API of the actual method is terrible and polluted with things not even needed any more */
    public async openOrderWindowFromTradeIdea(idea: BaseStrategy,
                                              portfolioId: number,
                                              showDisclaimer: boolean,
                                              targetPrice: number | null) {
        return this.openOrderWindow(idea, portfolioId, undefined, undefined, undefined, targetPrice, showDisclaimer);
    }

    public async openEquityOrderWindow(orderPrototype: Partial<EquityOrder>, accountId?: AccountId) {
        return this.equityTicketService.openWizard(orderPrototype, accountId);
    }

    // TODO portfolioType and accountName can be removed from this method as they aren't actually used
    public async openOrderWindow(whatIf: BaseStrategy,
                                 portfolioId?: number,
                                 portfolioType?: string,
                                 accountName?: string,
                                 today: Date = new Date(),
                                 targetPrice?: number | null,
                                 fromTradeIdea: boolean = false): Promise<Array<any>> {

        const currentUser = this.currentUserService.user;
        if (currentUser.isBranding(Branding.TF) && currentUser.backOffice === BackOffice.PHILLIP) {
            try {
                var tradesValidate = whatIf.trades.map(t => {
                    return {
                       code: t.code,
                       quantity: t.quantity
                   };
                });

                const anyWhatIf: any = whatIf;
                if(anyWhatIf.tradeDetails && anyWhatIf.equityTradeDetails){
                    const tradesDetails = anyWhatIf.tradeDetails?.map((t: any) => {
                                        return {
                                            code: t.code,
                                            quantity: t.total
                                        };
                                    });

                    const equityTradeDetails = anyWhatIf.equityTradeDetails?.map((t: any) => {
                                         return {
                                             code: t.underlyingCode,
                                             quantity: t.total
                                         };
                                     });
                    tradesValidate = tradesDetails?.concat(equityTradeDetails)!;
                }
                await this.validateTradingLevel(tradesValidate, portfolioId);
            } catch (error) {
                return [1];
            }
        }

        if (this.isSingleEquityOrder(whatIf)) {
            const trade = whatIf.trades[0];
            const code = trade.code;
            const quantity = trade.quantity;
            const side = quantity >= 0 ? OrderSide.BUY : OrderSide.LONG_SELL;
            const orderPrototype = {
                code,
                quantity: Math.abs(quantity),
                side
            };
            await this.equityTicketService.openWizard(orderPrototype);
            return [1]; // Cuz doing a good API didn't sound like a good idea to begin with...
        }
        // Unset the TMC on a copy of the object
        // The issue is that we are overloading TMC to mean 'is a locked strategy' and is a TMC
        whatIf = Object.assign({}, whatIf);
        if (whatIf.trades.length < 2) {
            whatIf.tmc = false;
        }

        let tmcOverride = false;

        if (whatIf.tmc) {
            try {
                whatIf.tmc = await this.openTmcTrainingVideo();
                if (whatIf.tmc) {
                    await this.validateTmc(whatIf.trades);
                }
            } catch (error) {
                whatIf.tmc = false;
            }
        } else if ((whatIf.trades.length > 1) && this.currentUserService.isClient()) {
            // We only warn clients if they are placing a set of single trades that could be TMC'd
            let validTmc = false;
            try {
                await this.validateTmc(whatIf.trades);
                validTmc = true;
            } catch (e) {
                // Nothing to do, not a valid TMC anyway
            }

            // Cancel here will cancel everything
            if (validTmc) {
                const action = await this.tmcWarningDialog.open();
                if (action === TmcWarningAction.TRADE_TMC) {
                    whatIf.tmc = true;
                } else if (action === TmcWarningAction.TRADE_SINGLES) {
                    tmcOverride = true;
                }
            }
        }

        try {
            const data = await this.optionTicketDialogService.open(whatIf, targetPrice, fromTradeIdea);
            if (whatIf.tmc) {
                const order = data.trades[0];
                return this.tradeService.placeTmcTrade(data.portfolioId, order);
            } else {
                return this.tradeService.placeTrades(data.portfolioId, data.trades, whatIf.name, tmcOverride);
            }
        } catch (error) {
            const message = error.data.error;
            await this.simpleModalService.error(null, message);
            return [1];
        }
    }

    private isSingleEquityOrder(whatIf: BaseStrategy): boolean {
        const trades = whatIf.trades;
        if (trades.length > 1) {
            return false;
        } else {
            return SecurityUtil.isEquity(trades[0].code);
        }
    }

    private async openTmcTrainingVideo(): Promise<boolean> {
        if (this.currentUserService.isGame() || this.currentUserService.hasWatchedTmcVideo()) {
            return true;
        }
        try {
            await this.tmcVideoModal.open();
            this.currentUserService.setWatchedTmcVideo();
            return true;
        } catch (error) {
            return false;
        }
    }

    public async validateLegs(input: TradingLevelRequest): Promise<void> {
        try {
            await this.tradingLevelService.post(input);
        } catch (error) {
            const message = error.data.error;
            await this.simpleModalService.error(null, message);
            return Promise.reject(message);
        }
    }

    public async validateTmc(input: Array<BaseTrade>): Promise<void> {
        const trades = input;

        try {
            const params = {
                scale: 1
            };

            await this.$http.post<void>("/tradefloor/api/orders/validate-tmc", trades, {params});
        } catch (error) {
            const message = error.data.error;
            await this.simpleModalService.error(null, message);
            return Promise.reject(message);
        }
    }

    public async validateTradingLevel(input: Array<BaseTrade>, portfolioId?: number): Promise<void> {
        const legs = input;
        if(portfolioId) {
            const request: TradingLevelRequest = {
                portfolioId,
                legs
            };
            try {
                await this.tradingLevelService.post(request);
            } catch (error) {
                const message = error;
                // await this.simpleModalService.error(null, "Trade is outside of your risk parameters");
                await this.simpleModalService.error(null, message);
                return Promise.reject(message);
            }
        } else {
            this.simpleModalService.error(null, "Portfolio is not available");
        }
    }

    public async simulatedTmcCode(legs: ReadonlyArray<TmcLeg>): Promise<TmcCodeResponse> {
        try {
            const url = `/tradefloor/api/simulated-orders/tmc`;
            const response = await this.$http.post<TmcCodeResponse>(url, legs);
            return response.data!;
        } catch (e) {
            await this.simpleModalService.error(null, "Unable to create a TMC at this time");
            return Promise.reject();
        }
    }

    public async tmcCode(legs: ReadonlyArray<TmcLeg>, portfolioId: number): Promise<TmcCodeResponse> {
        try {
            const url = `/tradefloor/api/orders/portfolio/${portfolioId}/tmc`;
            const response = await this.$http.post<TmcCodeResponse>(url, legs);
            return response.data!;
        } catch (e) {
            await this.simpleModalService.error(null, "Unable to create a TMC at this time");
            return Promise.reject();
        }
    }

    public async orderPlacers(portfolioId: number): Promise<Array<string>> {
        const url = `/tradefloor/api/orders/order-placers/${portfolioId}`;
        const response = await this.$http.get<Array<string>>(url);
        return response.data || [];
    }
}
